import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import es from '@/locales/es';
import en from '@/locales/en';
import fr from '@/locales/fr';
import pt from '@/locales/pt';
import zh from '@/locales/zh';
import ar from '@/locales/ar';

const LangCtx = createContext(null);

// Full registry of languages we have translations for. Do NOT delete entries
// here even if a language is currently inactive — translation/enrichment data
// already in Mongo would lose its labels. See ACTIVE_LANGS below for the
// runtime gate.
export const LANGUAGES = [
  { code: 'es', label: 'Español', flag: 'ES' },
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'fr', label: 'Français', flag: 'FR' },
  { code: 'pt', label: 'Português', flag: 'PT' },
  { code: 'zh', label: '中文', flag: 'ZH' },
  { code: 'ar', label: 'العربية', flag: 'AR' },
];

// Single source of truth for languages the app currently exposes. Other
// languages are kept dormant in the DB and code so we can re-enable them
// later without re-doing translations/enrichments. To re-enable PT/ZH/AR
// just add the codes back here AND in backend ACTIVE_LANGS.
export const ACTIVE_LANGS = ['es', 'en', 'fr'];
export const ACTIVE_LANGUAGES = LANGUAGES.filter((l) => ACTIVE_LANGS.includes(l.code));

// Translation dictionaries live in /src/locales/{code}.js — one file per
// language. Splitting reduces the top-level JS chunk by ~150KB and makes
// translation edits 30× easier to navigate.
const T = { es, en, fr, pt, zh, ar };

const SUPPORTED_CODES = ACTIVE_LANGS;

function pickDefaultLang() {
  if (typeof window === 'undefined') return 'es';
  // 1) Returning user — respect previous explicit choice IF it's still active.
  // If they had picked a language we've since disabled (pt/zh/ar), fall back.
  const stored = window.localStorage?.getItem('pm_lang');
  if (stored && SUPPORTED_CODES.includes(stored)) return stored;
  if (stored && !SUPPORTED_CODES.includes(stored)) {
    try { window.localStorage.setItem('pm_lang', 'es'); } catch {}
  }
  // 2) First visit — try every browser-preferred language in order
  const candidates = (window.navigator?.languages && window.navigator.languages.length
    ? window.navigator.languages
    : [window.navigator?.language || 'es']);
  for (const raw of candidates) {
    if (!raw) continue;
    const code = String(raw).toLowerCase().split('-')[0];
    if (SUPPORTED_CODES.includes(code)) return code;
  }
  return 'es';
}

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(pickDefaultLang);

  // Guard against attempts to switch to an inactive language (defensive).
  const setLang = (code) => {
    if (!SUPPORTED_CODES.includes(code)) return;
    setLangState(code);
  };

  useEffect(() => {
    localStorage.setItem('pm_lang', lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  const t = useMemo(() => (key, vars) => {
    const dict = T[lang] || T.es;
    const raw = dict[key] || T.es[key] || key;
    if (!vars) return raw;
    return raw.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
  }, [lang]);

  const value = { lang, setLang, t, languages: ACTIVE_LANGUAGES };
  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export const useLang = () => useContext(LangCtx);
